import { cloneDeep } from 'lodash'

const MODULE_NAME = 'undoHistory'

const types = {
  SET_PAST: 'SET_PAST',
  SET_PRESENT: 'SET_PRESENT',
  SET_FUTURE: 'SET_FUTURE'
}

const actionNames = {
  PUSH: 'PUSH',
  UNDO: 'UNDO',
  REDO: 'REDO',
  INITIALIZE: 'INITIALIZE'
}

export const actions = {
  PUSH: `${MODULE_NAME}/${actionNames.PUSH}`,
  UNDO: `${MODULE_NAME}/${actionNames.UNDO}`,
  REDO: `${MODULE_NAME}/${actionNames.REDO}`,
  INITIALIZE: `${MODULE_NAME}/${actionNames.INITIALIZE}`
}

/**
 * A helper filter that checks the current mutation against the array of provided mutation types.
 * @param { Array } mutations An array of mutation types to check against
 */
export const isMutationType = mutations => mutation => mutations.indexOf(mutation.type) !== -1

export default function createUndoHistoryModule (store, {
  initialValue = null,
  /**
   * A predicate accepting a mutation and state object. If it returns
   * true, the history will be recorded for the mutation.
   */
  filter = (mutation, state) => true,
  reducer = state => state,
  restoreState,
  undoLevels = 20
}, {namespace, add}) {
  // let ns = this.namespace + '__'
  let ns = ''
  let getterNames = {
    canUndo: ns + 'canUndo',
    canNotUndo: ns + 'canNotUndo',
    canRedo: ns + 'canRedo',
    canNotRedo: ns + 'canNotRedo'
  }
  let mutationNames = {
    setPast: ns + types.SET_PAST,
    setPresent: ns + types.SET_PRESENT,
    setFuture: ns + types.SET_FUTURE
  }
  store.subscribe((mutation, state) => {
    /**
     * looks like the namespace is used for the subs....,
     * only thing is, the subs should be namespaced as well...
     */
    // NAMESPACE the action for push is changed.
    // also need to add in the namespace as well..
    // I think this namespace should be the targetDefinition namespace.
    let n = add && namespace ? `${namespace}/` : ''
    if (filter(mutation, state) && !!state[namespace]) {
      store.dispatch(n + actions.PUSH, reducer(state))
    }
  })
  return {
    module: {
      [MODULE_NAME]: {
        namespaced: true,
        state: {
          ...createHistoryState(initialValue)
        },
        getters: {
          [getterNames.canUndo]: state => state.past.length > 0,
          [getterNames.canNotUndo]: (state, getters) => !getters[getterNames.canUndo],
          [getterNames.canRedo]: state => state.future.length > 0,
          [getterNames.canNotRedo]: (state, getters) => !getters[getterNames.canRedo]
        },
        mutations: {
          [mutationNames.setPast] (state, value) {
            state.past = value
          },
          [mutationNames.setPresent] (state, value) {
            state.present = value
          },
          [mutationNames.setFuture] (state, value) {
            state.future = value
          }
        },
        actions: {
          [actionNames.INITIALIZE] ({ commit, state }, value) {
            const { past, present, future } = createHistoryState(cloneDeep(value))
            commit(mutationNames.setPast, past)
            commit(mutationNames.setPresent, present)
            commit(mutationNames.setFuture, future)
          },
          [actionNames.PUSH] ({ commit, state }, value) {
            const { past, present } = state
            if (present === value) return
            if (present !== null) {
              commit(mutationNames.setPast, limitHistorySteps([...past, present], undoLevels))
            }
            commit(mutationNames.setPresent, cloneDeep(value))
            commit(mutationNames.setFuture, [])
          },
          [actionNames.UNDO] ({ commit, state }) {
            const { past, present, future } = state
            const previous = past[past.length - 1]
            const newPast = past.slice(0, past.length - 1)
            commit(mutationNames.setPast, newPast)
            commit(mutationNames.setPresent, previous)
            commit(mutationNames.setFuture, [present, ...future])
            restoreState(cloneDeep(state.present))
          },
          [actionNames.REDO] ({ commit, state }) {
            const { past, present, future } = state
            const next = future[0]
            const newFuture = future.slice(1)
            commit(mutationNames.setPast, [...past, present])
            commit(mutationNames.setPresent, next)
            commit(mutationNames.setFuture, newFuture)
            restoreState(cloneDeep(state.present))
          }
        }
      }
    }
  }
}

const createHistoryState = initialValue => ({
  past: [],
  present: initialValue,
  future: []
})

const limitHistorySteps = (history, steps) => history.slice(-steps)



// WEBPACK FOOTER //
// ./src/app/store/plugins/undoHistory.js